import { readSaTokenHeader } from '@glyphic/shared-core'

import { readApiBase } from '../api/config'
import { getAuthToken } from './session'

function saTokenHeaderName(): string {
  return readSaTokenHeader(
    () => import.meta.env.VITE_SA_TOKEN_HEADER as string | undefined,
    { dev: import.meta.env.DEV },
  )
}

export function dispatchSseBlock(
  block: string,
  onEvent: (eventName: string, data: string) => void,
): void {
  let eventName = 'message'
  const dataLines: string[] = []
  for (const line of block.split('\n')) {
    if (line.startsWith('event:')) {
      eventName = line.slice(6).trim()
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).trimStart())
    }
  }
  if (dataLines.length === 0) return
  onEvent(eventName, dataLines.join('\n'))
}

export type SubscribeSseJsonOptions<T> = {
  /** 相对 API 根的路径，如 `/v1/creations/events` */
  path: string
  eventName: string
  onPayload: (payload: T) => void
  onDisconnected?: () => void
}

/** fetch + ReadableStream 订阅 SSE（携带 satoken 请求头） */
export function subscribeSseJsonEvents<T>(options: SubscribeSseJsonOptions<T>): () => void {
  const ac = new AbortController()
  let closed = false

  void (async () => {
    const headers = new Headers({ Accept: 'text/event-stream' })
    const token = getAuthToken()
    if (token) {
      headers.set(saTokenHeaderName(), token)
    }
    const base = readApiBase().replace(/\/+$/, '')
    const path = options.path.startsWith('/') ? options.path : `/${options.path}`
    const url = `${base}${path}`
    try {
      const res = await fetch(url, { headers, signal: ac.signal })
      if (!res.ok || !res.body) {
        if (!closed) options.onDisconnected?.()
        return
      }
      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      while (!closed) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        let splitAt = buffer.indexOf('\n\n')
        while (splitAt >= 0) {
          const block = buffer.slice(0, splitAt)
          buffer = buffer.slice(splitAt + 2)
          dispatchSseBlock(block, (eventName, data) => {
            if (eventName !== options.eventName) return
            try {
              options.onPayload(JSON.parse(data) as T)
            } catch {
              /* ignore malformed payload */
            }
          })
          splitAt = buffer.indexOf('\n\n')
        }
      }
    } catch {
      if (!closed) options.onDisconnected?.()
      return
    }
    if (!closed) options.onDisconnected?.()
  })()

  return () => {
    closed = true
    ac.abort()
  }
}
